/**
 * ApiaProPanel — Destine AP-IA tab inside ProSpace (Ads).
 *
 * PRO users submit an application (project name, purpose, reason). Once an
 * admin approves it, the raw key appears here (only briefly) so the user can
 * copy it. All labels are translated across the 13 languages via `t('apia.…')`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Key, Loader2, Copy, ExternalLink, Send, Info, CheckCircle2, XCircle,
  Clock, AlertTriangle, ShieldCheck, Activity, RefreshCw,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const DOC_URL = 'https://info.destine.studio/document';

/** UsageGauge — live per-scope daily-quota bars for an approved AP-IA key. */
const UsageGauge = ({ k, t, language }) => {
  const perScope = Array.isArray(k.per_scope_usage) && k.per_scope_usage.length > 0
    ? k.per_scope_usage
    : [{
        scope: 'text_ai', labels: {},
        used_per_day: Number(k.usage_today || 0),
        used_per_min: Number(k.usage_this_minute || 0),
        quota_per_day: Number(k.quota_per_day || 1000),
        quota_per_min: Number(k.quota_per_min || 60),
        credits: Number(k.topup_credits || 0),
        quota_reached: Boolean(k.quota_reached),
      }];

  return (
    <div className="mt-3 p-3 rounded-lg bg-gradient-to-br from-black/40 to-fuchsia-900/10 border border-white/10 space-y-3" data-testid="apia-usage-gauge">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-mono text-xs text-fuchsia-300 select-all">{k.key_prefix}</span>
        {k.last_used_at && <span className="text-[10px] text-white/40">{t('apia.lastUsed')}: {k.last_used_at.slice(11, 16)}</span>}
      </div>
      {perScope.map(sc => {
        const label = sc.labels?.[language] || sc.labels?.fr || sc.scope;
        const today = Number(sc.used_per_day || 0);
        const perDay = Number(sc.quota_per_day || 1);
        const min = Number(sc.used_per_min || 0);
        const perMin = Number(sc.quota_per_min || 1);
        const pct = Math.min(100, Math.round((today / perDay) * 100));
        const full = today >= perDay;
        const bar = full ? 'from-red-500 to-red-600'
          : pct >= 80 ? 'from-amber-400 to-red-500'
          : pct >= 50 ? 'from-fuchsia-400 to-amber-400'
          : 'from-emerald-400 to-fuchsia-500';
        return (
          <div key={sc.scope} data-testid={`apia-usage-scope-${sc.scope}`}>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="font-semibold text-white/85">{label}</span>
              <span className="text-white/40 flex items-center gap-1"><Activity className="w-3 h-3" /> {min}/{perMin} min</span>
            </div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-white/60">{t('apia.usageToday') || 'Aujourd\'hui'}</span>
              <span className={`font-mono ${full ? 'text-red-300' : 'text-white/80'}`}>
                {today.toLocaleString()} / {perDay.toLocaleString()}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className={`h-full rounded-full bg-gradient-to-r ${bar} transition-all duration-500`} style={{ width: `${Math.max(2, pct)}%` }} data-testid={`apia-usage-bar-${sc.scope}`} />
            </div>
            <div className="mt-1 flex items-center justify-between text-[10.5px] text-white/40">
              {full ? (
                <span className="text-red-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t('apia.quotaFull') || 'Quota atteint'}</span>
              ) : (
                <span>{(perDay - today).toLocaleString()} {t('apia.quotaLeft') || 'restantes'}</span>
              )}
              {sc.credits > 0 && <span className="text-emerald-300">+{sc.credits} crédits</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatusBadge = ({ status, t }) => {
  const cfg = {
    pending:  { icon: Clock, cls: 'bg-amber-500/15 text-amber-300 border-amber-400/30', label: t('apia.statusPending') },
    approved: { icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30', label: t('apia.statusApproved') },
    refused:  { icon: XCircle, cls: 'bg-red-500/15 text-red-300 border-red-400/30', label: t('apia.statusRefused') },
  }[status] || { icon: Clock, cls: 'bg-white/10 text-white/60 border-white/15', label: status };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

/** TopupButtons — quota exhausted CTA + pack picker (Stripe checkout). */
const TopupButtons = ({ keyObj, t: _t }) => {
  const [packs, setPacks] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const reached = keyObj.quota_reached || (keyObj.usage_today >= keyObj.quota_per_day);
  const credits = keyObj.topup_credits || 0;

  const loadPacks = async () => {
    if (packs.length > 0) return;
    const r = await axios.get('/api/apia/packs');
    setPacks(r.data?.packs || []);
  };

  const buy = async (pack_code) => {
    setBusy(pack_code);
    try {
      const r = await axios.post('/api/apia/topup/checkout', { key_id: keyObj.id, pack_code });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur Stripe');
    } finally {
      setBusy(null);
    }
  };

  if (!reached && credits === 0) return null;

  return (
    <div className={`mt-2 p-3 rounded-lg border ${reached ? 'bg-red-500/10 border-red-400/40' : 'bg-emerald-500/10 border-emerald-400/30'}`} data-testid="apia-topup-box">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-xs">
          {reached ? (
            <span className="text-red-200 font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Quota gratuit atteint — recharge pour continuer</span>
          ) : (
            <span className="text-emerald-200">Solde de recharges : <strong>{credits.toLocaleString()}</strong> req</span>
          )}
        </div>
        <button
          onClick={() => { setOpen(v => !v); loadPacks(); }}
          className="text-xs px-3 py-1 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 flex items-center gap-1"
          data-testid="apia-topup-toggle"
        >
          {open ? 'Fermer' : (reached ? 'Recharger maintenant' : 'Recharger')}
        </button>
      </div>
      {open && (
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2" data-testid="apia-topup-packs">
          {packs.map(p => (
            <li key={p.code}>
              <button
                onClick={() => buy(p.code)}
                disabled={busy === p.code}
                className="w-full p-2 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 disabled:opacity-50 text-white text-xs font-semibold text-center"
                data-testid={`apia-topup-buy-${p.code}`}
              >
                {busy === p.code ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : p.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};



const ApiaProPanel = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ project_name: '', purpose: '', reason: '' });
  const [scopes, setScopes] = useState([]);           // catalog fetched from /apia/scopes
  const [selectedScopes, setSelectedScopes] = useState(['text_ai']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, scp] = await Promise.all([
        axios.get('/api/apia/my-requests'),
        axios.get('/api/apia/scopes').catch(() => ({ data: { scopes: [] } })),
      ]);
      setRequests(reqs.data?.requests || []);
      setScopes(scp.data?.scopes || []);
    } catch (e) {
      // If not PRO, backend returns 403 — surface via toast once
      if (e.response?.status === 403) {
        toast.error(t('apia.proOnly'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe checkout return — confirm & credit the key
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('apia_topup');
    const sid = params.get('session_id');
    if (status === 'success' && sid) {
      axios.post('/api/apia/topup/confirm', { session_id: sid })
        .then((r) => {
          if (r.data?.paid || r.data?.already_applied) {
            toast.success(`Recharge appliquée (+${r.data.credited || 0} req)`);
            load();
          }
        })
        .catch(() => {/* silent */})
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname);
        });
    } else if (status === 'cancelled') {
      toast('Paiement annulé');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [load]);

  // Auto-refresh usage counters every 15s when at least one approved+active key exists
  useEffect(() => {
    const hasActiveKey = requests.some(r => r.status === 'approved' && r.key && !r.key.revoked_at);
    if (!hasActiveKey) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [requests, load]);

  const submit = async () => {
    const { project_name, purpose, reason } = form;
    if (!project_name.trim() || purpose.trim().length < 10 || reason.trim().length < 10) {
      toast.error(t('apia.purposePh'));
      return;
    }
    if (selectedScopes.length === 0) {
      toast.error('Sélectionne au moins une capacité');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/api/apia/requests', {
        project_name: project_name.trim(),
        purpose: purpose.trim(),
        reason: reason.trim(),
        scopes: selectedScopes,
      });
      toast.success(t('apia.submitted'));
      setForm({ project_name: '', purpose: '', reason: '' });
      setSelectedScopes(['text_ai']);
      await load();
    } catch (e) {
      const s = e.response?.status;
      if (s === 409) toast.error(t('apia.alreadyPending'));
      else if (s === 403) toast.error(t('apia.proOnly'));
      else toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleScope = (id) => {
    setSelectedScopes(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const copyKey = async (raw) => {
    try {
      await navigator.clipboard.writeText(raw);
      toast.success(t('apia.copied'));
    } catch {
      toast.error('Copy failed');
    }
  };

  const hasPending = requests.some(r => r.status === 'pending');

  return (
    <div className="space-y-4" data-testid="apia-pro-panel">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-fuchsia-500/15 to-indigo-500/15 border border-fuchsia-400/30 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-fuchsia-200 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-white text-base mb-1">{t('apia.panelTitle')}</div>
            <p className="text-white/80">{t('apia.panelSubtitle')}</p>
            <a
              href={DOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-fuchsia-300 hover:text-fuchsia-200 text-sm font-medium underline"
              data-testid="apia-doc-link"
            >
              <ExternalLink className="w-3.5 h-3.5" /> {t('apia.docLink')}
            </a>
            <p className="mt-2 text-amber-200/80 text-xs flex items-start gap-1">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {t('apia.freeInfo')} · {t('apia.quotaInfo')}
            </p>
          </div>
        </div>
      </div>

      {/* Application form (hidden if a pending request already exists) */}
      {!hasPending && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <Key className="w-4 h-4 text-fuchsia-300" /> {t('apia.requestTitle')}
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-white/60 block mb-1">{t('apia.projectName')}</label>
              <input
                data-testid="apia-project-input"
                value={form.project_name}
                onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))}
                placeholder={t('apia.projectNamePh')}
                className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">{t('apia.purpose')}</label>
              <textarea
                data-testid="apia-purpose-input"
                rows={3}
                value={form.purpose}
                onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                placeholder={t('apia.purposePh')}
                className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">{t('apia.reason')}</label>
              <textarea
                data-testid="apia-reason-input"
                rows={3}
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder={t('apia.reasonPh')}
                className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400"
              />
            </div>
            {/* v50.5 — capability scopes */}
            {scopes.length > 0 && (
              <div data-testid="apia-scopes-section">
                <label className="text-xs text-white/60 block mb-1.5">Capacités demandées <span className="text-white/40">(1 ou plusieurs)</span></label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {scopes.map(sc => {
                    const label = sc.labels?.[language] || sc.labels?.fr || sc.id;
                    const desc = sc.descriptions?.[language] || sc.descriptions?.fr || '';
                    const checked = selectedScopes.includes(sc.id);
                    return (
                      <label
                        key={sc.id}
                        className={`cursor-pointer rounded-xl border p-3 transition-all ${checked ? 'border-fuchsia-400 bg-fuchsia-500/15' : 'border-white/15 bg-white/5 hover:border-white/30'}`}
                        data-testid={`apia-scope-option-${sc.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <input type="checkbox" checked={checked} onChange={() => toggleScope(sc.id)} className="mt-0.5" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
                              <span>{label}</span>
                              {!sc.available && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-200">Bientôt</span>}
                            </div>
                            <p className="text-[11px] text-white/60 mt-0.5 leading-snug">{desc}</p>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <button
              data-testid="apia-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? t('apia.submitting') : t('apia.submit')}
            </button>
          </div>
        </div>
      )}

      {/* My requests list */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-white">{t('apia.myRequests')}</h4>
          {requests.some(r => r.status === 'approved' && r.key && !r.key.revoked_at) && (
            <button
              onClick={load}
              disabled={loading}
              className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors disabled:opacity-40"
              title="Refresh"
              data-testid="apia-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/50" /></div>
        ) : requests.length === 0 ? (
          <div className="text-sm text-white/40 text-center py-6">{t('apia.empty')}</div>
        ) : (
          <ul className="space-y-3" data-testid="apia-my-requests-list">
            {requests.map(r => (
              <li key={r.id} className="rounded-xl bg-black/20 border border-white/10 p-3" data-testid={`apia-req-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white">{r.project_name}</span>
                      <StatusBadge status={r.status} t={t} />
                    </div>
                    <div className="text-[11px] text-white/40 mt-0.5">{r.created_at?.slice(0, 16).replace('T', ' ')}</div>
                    <div className="text-xs text-white/70 mt-2 whitespace-pre-wrap break-words">{r.purpose}</div>
                  </div>
                </div>

                {/* Approved → show raw key ONCE */}
                {r.status === 'approved' && r.raw_key && !r.key?.revoked_at && (
                  <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border-2 border-emerald-400/50">
                    <div className="text-xs font-semibold text-emerald-300 mb-1">{t('apia.yourKey')}</div>
                    <div className="flex items-center gap-2 p-2 rounded bg-black/40 font-mono text-xs text-emerald-200 select-all break-all">
                      {r.raw_key}
                      <button onClick={() => copyKey(r.raw_key)} className="p-1 rounded hover:bg-white/10 flex-shrink-0" title={t('apia.copy')}>
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-[11px] text-amber-200 mt-2 flex items-start gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {t('apia.keyOnceWarning')}
                    </p>
                  </div>
                )}

                {/* Approved & not revoked → live usage gauge */}
                {r.status === 'approved' && r.key && !r.key.revoked_at && (
                  <>
                    <UsageGauge k={r.key} t={t} language={language} />
                    <TopupButtons keyObj={r.key} t={t} />
                  </>
                )}

                {/* Approved but revoked */}
                {r.status === 'approved' && r.key?.revoked_at && (
                  <div className="mt-3 text-xs text-red-300 flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" /> {t('apia.revoked')} · {r.key.key_prefix}
                  </div>
                )}

                {/* Refused → show reason */}
                {r.status === 'refused' && (
                  <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-xs">
                    <div className="text-red-300 font-semibold mb-1">{t('apia.refusedBy')}</div>
                    <div className="text-white/70"><strong>{t('apia.refuseReason')}:</strong> {r.refuse_reason}</div>
                  </div>
                )}

                {r.admin_note && r.status === 'approved' && (
                  <div className="mt-2 text-xs text-white/50"><strong>{t('apia.refuseReason')}:</strong> {r.admin_note}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ApiaProPanel;
